/**
 * In hoá đơn (phản ánh 30/09): dựng tờ in từ hoá đơn thật của máy chủ, rồi đưa ra hai đường —
 *   1. hộp in của trình duyệt (mọi máy in đã thêm vào hệ điều hành: WiFi, USB, Bluetooth);
 *   2. gửi thẳng lệnh ESC/POS tới máy in nhiệt qua USB / Bluetooth (xem `may-in.ts`).
 *
 * Tờ in là ẢNH khi gửi thẳng (vẽ canvas → điểm ảnh đen trắng): máy in nhiệt rẻ tiền không có bảng mã tiếng Việt,
 * gửi chữ thô là ra ký tự rác. Vẽ thành ảnh thì có dấu đúng ở mọi máy in.
 *
 * Tiền đi qua `tienVnd` (chuỗi số nguyên đồng) — không tính lại ở đây, chỉ in đúng số máy chủ đã chốt.
 */
import { type HoaDon, hienSdt, tenKhach, tienVnd } from './api.ts'
import { IN } from './text/in-hoa-don.ts'
import { S } from './text/so-tien.ts'

export type KhoGiay = 58 | 80

/** Một dòng của tờ in. `phai` có → hai cột (trái nhãn, phải số tiền). */
export interface DongIn {
  trai: string
  phai?: string
  can?: 'giua' | 'trai'
  dam?: boolean
  /** Chữ nhỏ (ghi chú, địa chỉ). */
  nho?: boolean
  /** Kẻ ngang nét đứt thay cho chữ. */
  ke?: boolean
  /** Chữ to (tên tiệm, tổng cộng). */
  to?: boolean
}

/** "05/10/2026 14:05" theo giờ Việt Nam, không phụ thuộc múi giờ của máy in / trình duyệt. */
export function gioVietTuIso(iso: string): string {
  const d = new Date(iso)
  const p = new Intl.DateTimeFormat('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d)
  const lay = (k: string) => p.find((x) => x.type === k)?.value ?? ''
  return `${lay('day')}/${lay('month')}/${lay('year')} ${lay('hour')}:${lay('minute')}`
}

const KE: DongIn = { trai: '', ke: true }

export function dungBienLai(hd: HoaDon, tenTiem: string): DongIn[] {
  const tt = hd.inThongTin
  const dong: DongIn[] = []
  dong.push({ trai: tenTiem, can: 'giua', dam: true, to: true })
  if (tt.chiNhanh !== null && tt.chiNhanh !== tenTiem) {
    dong.push({ trai: tt.chiNhanh, can: 'giua' })
  }
  if (tt.diaChi !== null && tt.diaChi !== '') dong.push({ trai: tt.diaChi, can: 'giua', nho: true })
  if (tt.sdtChiNhanh !== null) {
    dong.push({ trai: hienSdt(tt.sdtChiNhanh), can: 'giua', nho: true })
  }
  dong.push(KE)
  dong.push({ trai: `${IN.soHoaDon}: ${hd.number}`, dam: true })
  dong.push({ trai: `${IN.ngay}: ${gioVietTuIso(hd.paidAt)}` })
  dong.push({ trai: `${IN.khach}: ${hd.khach === null ? IN.khachLe : tenKhach(hd.khach)}` })
  if (hd.thuNgan !== null) dong.push({ trai: `${IN.thuNgan}: ${hd.thuNgan}` })
  dong.push(KE)
  for (const d of hd.dong) {
    dong.push({
      trai: d.label,
      phai: d.paidBy === 'package' ? IN.truGoi : tienVnd(d.priceVnd),
    })
    if (d.ktv !== null) dong.push({ trai: `  ${d.ktv}`, nho: true })
  }
  dong.push(KE)
  if (hd.discountVnd !== '0') {
    dong.push({ trai: IN.tamTinh, phai: tienVnd(hd.subtotalVnd) })
    const lyDo =
      hd.discountReason === null || hd.discountReason === '' ? '' : ` (${hd.discountReason})`
    dong.push({ trai: `${IN.giam}${lyDo}`, phai: `-${tienVnd(hd.discountVnd)}` })
  }
  dong.push({ trai: IN.tong, phai: tienVnd(hd.totalVnd), dam: true, to: true })
  if (hd.traBang.length > 0) {
    dong.push(KE)
    for (const t of hd.traBang) {
      dong.push({ trai: S.cach[t.method] ?? t.method, phai: tienVnd(t.soVnd) })
    }
  }
  dong.push(KE)
  dong.push({ trai: IN.camOn, can: 'giua', dam: true })
  // Câu cố định theo yêu cầu chủ dự án: nêu số chủ tiệm để khách xin hoá đơn điện tử trong 24 giờ.
  dong.push({
    trai: IN.hoaDonDienTu(tt.sdtChuTiem === null ? null : hienSdt(tt.sdtChuTiem)),
    can: 'giua',
    nho: true,
  })
  return dong
}

/** Tờ in thử: chứng minh máy in nối được và in đúng tiếng Việt có dấu. */
export function dungBienLaiThu(tenTiem: string): DongIn[] {
  return [
    { trai: tenTiem, can: 'giua', dam: true, to: true },
    { trai: IN.tenThuNghiem, can: 'giua', dam: true },
    KE,
    { trai: 'Gội đầu dưỡng sinh', phai: tienVnd('120000') },
    { trai: 'Cắt tóc nữ', phai: tienVnd('250000') },
    { trai: IN.tong, phai: tienVnd('370000'), dam: true, to: true },
    KE,
    { trai: IN.inThuDong, can: 'giua', nho: true },
  ]
}

// ------------------------------------------------------------------ vẽ ảnh + ESC/POS

/** Số điểm ngang của đầu in: 58 mm ≈ 384, 80 mm ≈ 576 (máy in nhiệt 203 dpi). */
export const DIEM_NGANG: Record<KhoGiay, number> = { 58: 384, 80: 576 }

interface KieuChu {
  px: number
  dam: boolean
}
function kieuChu(d: DongIn, rong: number): KieuChu {
  const goc = rong >= 576 ? 26 : 22
  if (d.to === true) return { px: goc + 6, dam: true }
  if (d.nho === true) return { px: goc - 5, dam: d.dam === true }
  return { px: goc, dam: d.dam === true }
}

/** Ngắt chữ cho vừa bề ngang (theo từ; từ dài hơn cả dòng thì cắt cứng). */
export function ngatDong(chu: string, rongToiDa: number, doRong: (s: string) => number): string[] {
  const ra: string[] = []
  let hienTai = ''
  for (const tu of chu.split(/\s+/).filter((x) => x !== '')) {
    const thu = hienTai === '' ? tu : `${hienTai} ${tu}`
    if (doRong(thu) <= rongToiDa) {
      hienTai = thu
      continue
    }
    if (hienTai !== '') ra.push(hienTai)
    let con = tu
    while (doRong(con) > rongToiDa && con.length > 1) {
      let n = con.length - 1
      while (n > 1 && doRong(con.slice(0, n)) > rongToiDa) n -= 1
      ra.push(con.slice(0, n))
      con = con.slice(n)
    }
    hienTai = con
  }
  if (hienTai !== '') ra.push(hienTai)
  return ra.length === 0 ? [''] : ra
}

/** Vẽ tờ in lên canvas trắng nền đen chữ. Chạy trong trình duyệt (cần `document`). */
export function veBienLai(dong: DongIn[], khoGiay: KhoGiay): HTMLCanvasElement {
  const rong = DIEM_NGANG[khoGiay]
  const le = 6
  const dungDo = document.createElement('canvas')
  dungDo.width = rong
  dungDo.height = 10
  const ctxDo = dungDo.getContext('2d')
  if (ctxDo === null) throw new Error('canvas')
  const font = (k: KieuChu) =>
    `${k.dam ? '700' : '400'} ${k.px}px system-ui, "Segoe UI", Arial, sans-serif`
  const doRongChu = (k: KieuChu) => (s: string) => {
    ctxDo.font = font(k)
    return ctxDo.measureText(s).width
  }

  // Lượt 1: đo chiều cao.
  interface Ve {
    d: DongIn
    k: KieuChu
    khuc: string[]
    y: number
    cao: number
  }
  const ds: Ve[] = []
  let y = 8
  for (const d of dong) {
    const k = kieuChu(d, rong)
    if (d.ke === true) {
      ds.push({ d, k, khuc: [], y, cao: 14 })
      y += 14
      continue
    }
    const vung = rong - le * 2
    const rongPhai = d.phai === undefined ? 0 : doRongChu(k)(d.phai) + 12
    const khuc = ngatDong(d.trai, vung - rongPhai, doRongChu(k))
    const cao = Math.round(k.px * 1.3) * khuc.length
    ds.push({ d, k, khuc, y, cao })
    y += cao
  }
  y += 24

  const canvas = document.createElement('canvas')
  canvas.width = rong
  canvas.height = y
  const ctx = canvas.getContext('2d')
  if (ctx === null) throw new Error('canvas')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, rong, y)
  ctx.fillStyle = '#000'
  ctx.textBaseline = 'top'
  for (const v of ds) {
    if (v.d.ke === true) {
      ctx.fillRect(le, v.y + 6, rong - le * 2, 2)
      continue
    }
    ctx.font = font(v.k)
    const dongCao = Math.round(v.k.px * 1.3)
    v.khuc.forEach((chu, i) => {
      const yy = v.y + i * dongCao
      if (v.d.can === 'giua') {
        ctx.textAlign = 'center'
        ctx.fillText(chu, rong / 2, yy)
      } else {
        ctx.textAlign = 'left'
        ctx.fillText(chu, le, yy)
      }
    })
    if (v.d.phai !== undefined) {
      ctx.textAlign = 'right'
      ctx.fillText(v.d.phai, rong - le, v.y)
    }
  }
  return canvas
}

/**
 * Điểm ảnh RGBA → lệnh ESC/POS in ảnh (`GS v 0`), cắt thành dải 128 dòng để máy in nhỏ bộ nhớ vẫn nhận đủ.
 * Đầu vào tách khỏi canvas để kiểm thử được bằng số.
 */
export function anhSangEscPos(rgba: Uint8ClampedArray, rong: number, cao: number): Uint8Array {
  const byteMoiDong = Math.ceil(rong / 8)
  const ra: number[] = [0x1b, 0x40] // ESC @ — khởi tạo
  const DAI = 128
  for (let y0 = 0; y0 < cao; y0 += DAI) {
    const h = Math.min(DAI, cao - y0)
    ra.push(0x1d, 0x76, 0x30, 0x00, byteMoiDong & 0xff, byteMoiDong >> 8, h & 0xff, h >> 8)
    for (let y = y0; y < y0 + h; y++) {
      for (let bx = 0; bx < byteMoiDong; bx++) {
        let b = 0
        for (let bit = 0; bit < 8; bit++) {
          const x = bx * 8 + bit
          if (x >= rong) continue
          const i = (y * rong + x) * 4
          const a = rgba[i + 3] ?? 255
          const sang =
            ((rgba[i] ?? 255) * 0.299 +
              (rgba[i + 1] ?? 255) * 0.587 +
              (rgba[i + 2] ?? 255) * 0.114) *
              (a / 255) +
            255 * (1 - a / 255)
          if (sang < 150) b |= 0x80 >> bit
        }
        ra.push(b)
      }
    }
  }
  ra.push(0x1b, 0x64, 0x04) // ESC d 4 — đẩy giấy 4 dòng
  ra.push(0x1d, 0x56, 0x42, 0x00) // GS V B 0 — cắt giấy (máy không có dao thì bỏ qua)
  return Uint8Array.from(ra)
}

export function bienLaiSangEscPos(dong: DongIn[], khoGiay: KhoGiay): Uint8Array {
  const canvas = veBienLai(dong, khoGiay)
  const ctx = canvas.getContext('2d')
  if (ctx === null) throw new Error('canvas')
  const anh = ctx.getImageData(0, 0, canvas.width, canvas.height)
  return anhSangEscPos(anh.data, canvas.width, canvas.height)
}

// ------------------------------------------------------------------ in bằng hộp in của trình duyệt

/**
 * Dựng tờ in bằng DOM thuần (`textContent`, KHÔNG innerHTML: tên khách / dịch vụ là chữ do người khác gõ),
 * gọi `window.print()`, rồi gỡ. CSS `@media print` ở styles.css chỉ cho `#in-bien-lai` hiện ra.
 */
export function inBangTrinhDuyet(dong: DongIn[], khoGiay: KhoGiay): void {
  const goc = document.createElement('div')
  goc.id = 'in-bien-lai'
  goc.style.width = `${khoGiay}mm`
  for (const d of dong) {
    if (d.ke === true) {
      const hr = document.createElement('div')
      hr.className = 'bl-ke'
      goc.appendChild(hr)
      continue
    }
    const h = document.createElement('div')
    h.className = `bl-dong${d.can === 'giua' ? ' giua' : ''}${d.dam === true ? ' dam' : ''}${
      d.nho === true ? ' nho' : ''
    }${d.to === true ? ' to' : ''}`
    const trai = document.createElement('span')
    trai.textContent = d.trai
    h.appendChild(trai)
    if (d.phai !== undefined) {
      const phai = document.createElement('b')
      phai.textContent = d.phai
      h.appendChild(phai)
    }
    goc.appendChild(h)
  }
  const kieu = document.createElement('style')
  kieu.id = 'in-bien-lai-trang'
  kieu.textContent = `@media print { @page { size: ${khoGiay}mm auto; margin: 2mm; } }`
  document.head.appendChild(kieu)
  document.body.appendChild(goc)
  const don = () => {
    goc.remove()
    kieu.remove()
    window.removeEventListener('afterprint', don)
  }
  window.addEventListener('afterprint', don)
  window.print()
  // Trình duyệt không bắn `afterprint` (một số bản di động): dọn sau một lúc, không để tờ in nằm lại trang.
  setTimeout(don, 60_000)
}
